import { useState, type CSSProperties } from 'react';
import { ColorPicker, ColorSwatch, IconButton, OpacityInput, Popover, Tooltip } from '@nula/core-ui';
import { CANVAS_COLORS } from '@/features/canvas/utils/constants';
import styles from './color-swatch-popover.module.css';

export interface ColorSwatchPopoverProps {
  label: string;
  value: string;
  onChange: (color: string) => void;
  /** Optional: when both opacity and onOpacityChange are provided, the
   *  popover renders an OpacityInput row above the swatch grid. */
  opacity?: number;
  onOpacityChange?: (v: number) => void;
}

/**
 * Reusable swatch popover used by node-formatting and edge-formatting
 * toolbars. Single trigger IconButton showing the current color as a
 * filled dot; popover opens with an 11-cell swatch grid plus a custom
 * color picker on the trailing cell.
 */
export function ColorSwatchPopover({
  label,
  value,
  onChange,
  opacity,
  onOpacityChange,
}: ColorSwatchPopoverProps) {
  const [open, setOpen] = useState(false);

  const content = (
    <div className={styles.colorPopover}>
      {onOpacityChange && opacity !== undefined && (
        <OpacityInput value={opacity} onChange={onOpacityChange} color={value} />
      )}
      <ColorSwatchGrid value={value} onChange={onChange} />
    </div>
  );

  return (
    <Popover content={content} trigger="click" placement="top" open={open} onOpenChange={setOpen}>
      <Tooltip title={label} placement="top" open={open ? false : undefined}>
        <IconButton transparent aria-label={label} className={styles.swatchBtn}>
          <span
            className={styles.swatchDot}
            style={{ '--swatch-color': value } as CSSProperties}
          />
        </IconButton>
      </Tooltip>
    </Popover>
  );
}

export function ColorSwatchGrid({
  value,
  onChange,
}: {
  value: string;
  onChange: (color: string) => void;
}) {
  return (
    <div className={styles.swatchGrid}>
      {CANVAS_COLORS.map((hex) => (
        <ColorSwatch key={hex} color={hex} active={value === hex} onClick={() => onChange(hex)} />
      ))}
      <div className={styles.swatchCellCustomLast}>
        <CustomColorButton value={value} onChange={onChange} />
      </div>
    </div>
  );
}

function CustomColorButton({ value, onChange }: { value: string; onChange: (hex: string) => void }) {
  return (
    <Tooltip title="Custom color" placement="top">
      <div className={styles.swatchCellCustom} onMouseDown={(e) => e.stopPropagation()}>
        <ColorPicker value={value} showText={false} size="sm" presets={[]} onChange={onChange} />
      </div>
    </Tooltip>
  );
}
